import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";

const contactSchema = z.object({
  name: z.string().trim().min(1, "Name is required").max(100, "Name is too long"),
  email: z.string().trim().email("Please enter a valid email").max(255, "Email is too long"),
  phone: z.string().trim().max(50).optional(),
  subject: z.string().trim().max(150).optional(),
  message: z.string().trim().min(1, "Message is required").max(2000, "Message is too long"),
  /** Honeypot: must stay empty. */
  website: z.string().max(200).optional(),
  /** Client timestamp (ms) when the form was rendered. */
  startedAt: z.number().optional(),
});

export type ContactInput = z.infer<typeof contactSchema>;

function createSupabaseFetch(supabaseKey: string): typeof fetch {
  return (input, init) => {
    const headers = new Headers(
      typeof Request !== "undefined" && input instanceof Request ? input.headers : undefined,
    );
    if (init?.headers) {
      new Headers(init.headers).forEach((value, key) => headers.set(key, value));
    }
    // New Supabase publishable keys are opaque strings, not bearer JWTs.
    if (headers.get("Authorization") === `Bearer ${supabaseKey}`) {
      headers.delete("Authorization");
    }
    headers.set("apikey", supabaseKey);
    return fetch(input, { ...init, headers });
  };
}

export const submitContactMessage = createServerFn({ method: "POST" })
  .inputValidator((data) => contactSchema.parse(data))
  .handler(async ({ data }) => {
    const { checkFormRateLimit, RATE_LIMIT_MESSAGE } = await import("./rate-limit.server");

    const tooFast =
      typeof data.startedAt === "number" && Date.now() - data.startedAt < 2500;
    if ((data.website && data.website.trim() !== "") || tooFast) {
      return { success: true };
    }

    if (!(await checkFormRateLimit("contact", 5, 60))) {
      throw new Error(RATE_LIMIT_MESSAGE);
    }

    const { createClient } = await import("@supabase/supabase-js");
    const supabaseUrl = process.env["SUPABASE_URL"]!;
    const supabaseKey = process.env["SUPABASE_PUBLISHABLE_KEY"]!;

    if (!supabaseUrl || !supabaseKey) {
      throw new Error("Server configuration error. Please try again later.");
    }

    const supabase = createClient(supabaseUrl, supabaseKey, {
      global: { fetch: createSupabaseFetch(supabaseKey) },
      auth: { autoRefreshToken: false, persistSession: false },
    });

    const { error } = await supabase.from("contact_messages").insert({
      name: data.name,
      email: data.email,
      phone: data.phone || null,
      subject: data.subject || null,
      message: data.message,
    });

    if (error) {
      console.error("Contact insert error:", error);
      throw new Error("Failed to send your message. Please try again later.");
    }

    // Notify the team by email. Never fail the submission if delivery fails —
    // the enquiry is already stored.
    try {
      const { sendTemplateEmail } = await import("./email-templates/send-email");
      const submittedAt = new Date().toLocaleString("en-ZA", {
        timeZone: "Africa/Johannesburg",
        dateStyle: "long",
        timeStyle: "short",
      });
      await sendTemplateEmail("contact-notification", "contact@adapted.info", {
        templateData: {
          name: data.name,
          email: data.email,
          phone: data.phone || "",
          subject: data.subject || "",
          message: data.message,
          submittedAt: `${submittedAt} SAST`,
        },
        replyTo: data.email,
        idempotencyKey: `contact-notification-${data.email}-${data.startedAt ?? Date.now()}`,
      });
    } catch (emailError) {
      console.error("Contact notification email error:", emailError);
    }

    return { success: true };
  });
